import { useState, useEffect } from "react";
import { DashboardLayout } from "@/pages/Dashboard";
import { useAuth, useLanguage, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";
import { 
  Users, Car, Home, FileText, Check, X, Eye, Shield, TrendingUp, 
  DollarSign, CreditCard, RefreshCw, Database, MapPin, Calendar, 
  Plane, Building, RotateCcw, Search, Filter, Sparkles, Briefcase, Verified, Award
} from "lucide-react";
import AdminAttractions from "@/pages/AdminAttractions";
import AdminGuide from "@/components/AdminGuide";

export default function AdminDashboard() {
  const { token } = useAuth();
  const { t, language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [dbSummary, setDbSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [driverApps, setDriverApps] = useState([]);
  const [payments, setPayments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [providers, setProviders] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [refundingId, setRefundingId] = useState(null);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, dbRes, usersRes, appsRes, paymentsRes, bookingsRes, propsRes, vehiclesRes, subsRes, providersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/database/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/driver-applications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/payments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/bookings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/properties`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/vehicles`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/subscriptions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/providers`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStats(statsRes.data);
      setDbSummary(dbRes.data);
      setUsers(usersRes.data);
      setDriverApps(appsRes.data);
      setPayments(paymentsRes.data);
      setBookings(bookingsRes.data);
      setProperties(propsRes.data);
      setVehicles(vehiclesRes.data);
      setSubscriptions(subsRes.data);
      setProviders(providersRes.data);
    } catch (error) {
      toast.error(t("فشل تحميل البيانات", "Failed to load data"));
    } finally {
      setLoading(false);
    }
  };

  const reviewApplication = async (appId, action) => {
    try {
      await axios.post(`${API}/admin/driver-applications/${appId}/review?action=${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(action === "approve" ? t("تمت الموافقة", "Approved") : t("تم الرفض", "Rejected"));
      loadAllData();
    } catch (error) {
      toast.error(t("فشلت العملية", "Operation failed"));
    }
  };

  const processRefund = async (sessionId) => {
    setRefundingId(sessionId);
    try {
      await axios.post(`${API}/admin/payments/${sessionId}/refund`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t("تم استرداد المبلغ", "Refund processed"));
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("فشل الاسترداد", "Refund failed"));
    } finally {
      setRefundingId(null);
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.post(`${API}/admin/bookings/${bookingId}/status?new_status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t("تم التحديث", "Updated"));
      loadAllData();
    } catch (error) {
      toast.error(t("فشل التحديث", "Update failed"));
    }
  };

  const updatePropertyStatus = async (id, status) => {
    try {
      await axios.post(`${API}/admin/properties/${id}/status?new_status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t("تم التحديث", "Updated"));
      loadAllData();
    } catch (e) { toast.error(t("فشل التحديث", "Update failed")); }
  };

  const deleteProperty = async (id) => {
    if (!window.confirm(t("تأكيد الحذف؟", "Confirm delete?"))) return;
    try {
      await axios.delete(`${API}/admin/properties/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("تم الحذف", "Deleted"));
      loadAllData();
    } catch (e) { toast.error(t("فشل الحذف", "Delete failed")); }
  };

  const updateVehicleStatus = async (id, status) => {
    try {
      await axios.post(`${API}/admin/vehicles/${id}/status?new_status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t("تم التحديث", "Updated"));
      loadAllData();
    } catch (e) { toast.error(t("فشل التحديث", "Update failed")); }
  };

  const deleteVehicle = async (id) => {
    if (!window.confirm(t("تأكيد الحذف؟", "Confirm delete?"))) return;
    try {
      await axios.delete(`${API}/admin/vehicles/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("تم الحذف", "Deleted"));
      loadAllData();
    } catch (e) { toast.error(t("فشل الحذف", "Delete failed")); }
  };

  const updateProviderStatus = async (id, status) => {
    try {
      await axios.post(`${API}/admin/providers/${id}/status?new_status=${status}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("تم التحديث", "Updated"));
      loadAllData();
    } catch (e) { toast.error(t("فشل التحديث", "Update failed")); }
  };

  const toggleProviderFlag = async (id, flag, value) => {
    try {
      await axios.post(`${API}/admin/providers/${id}/${flag}?${flag}=${value}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("تم", "Done"));
      loadAllData();
    } catch (e) { toast.error(t("فشل", "Failed")); }
  };

  const addAdmin = async () => {
    if (!newAdminEmail) {
      toast.error(t("أدخل البريد الإلكتروني", "Enter email"));
      return;
    }
    try {
      await axios.post(`${API}/admin/add-admin?email=${newAdminEmail}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t("تمت إضافة المدير", "Admin added"));
      setNewAdminEmail("");
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("فشلت العملية", "Operation failed"));
    }
  };

  const statCards = [
    { icon: Users, label: t("المستخدمين", "Users"), value: dbSummary?.users?.total || 0, color: "text-blue-400", bg: "bg-blue-500/20" },
    { icon: Car, label: t("السائقين", "Drivers"), value: dbSummary?.users?.drivers || 0, color: "text-green-400", bg: "bg-green-500/20" },
    { icon: FileText, label: t("طلبات معلقة", "Pending"), value: dbSummary?.users?.pending_drivers || 0, color: "text-yellow-400", bg: "bg-yellow-500/20" },
    { icon: Building, label: t("العقارات", "Properties"), value: dbSummary?.properties || 0, color: "text-purple-400", bg: "bg-purple-500/20" },
    { icon: Plane, label: t("الحجوزات", "Bookings"), value: dbSummary?.bookings || 0, color: "text-cyan-400", bg: "bg-cyan-500/20" },
    { icon: CreditCard, label: t("الاشتراكات", "Subscriptions"), value: dbSummary?.subscriptions?.active || 0, color: "text-pink-400", bg: "bg-pink-500/20" },
    { icon: DollarSign, label: t("المدفوعات", "Payments"), value: dbSummary?.payments?.total || 0, color: "text-emerald-400", bg: "bg-emerald-500/20" },
    { icon: TrendingUp, label: t("الإيرادات", "Revenue"), value: `$${dbSummary?.payments?.revenue || 0}`, color: "text-yellow-400", bg: "bg-yellow-500/20" },
  ];

  if (loading) {
    return (
      <DashboardLayout title={t("لوحة التحكم", "Admin Dashboard")}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <div key={i} className="glass-card rounded-xl p-6 h-28 skeleton"></div>
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={t("لوحة التحكم", "Admin Dashboard")}>
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Refresh Button */}
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold text-white">{t("نظرة عامة", "Overview")}</h2>
          <Button onClick={loadAllData} className="btn-secondary">
            <RefreshCw size={18} className="ml-2" />
            {t("تحديث", "Refresh")}
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {statCards.map((stat, i) => (
            <div key={i} className="glass-card rounded-xl p-5">
              <div className={`w-10 h-10 ${stat.bg} rounded-lg flex items-center justify-center mb-3`}>
                <stat.icon className={stat.color} size={22} />
              </div>
              <p className="text-2xl font-bold text-white">{stat.value}</p>
              <p className="text-slate-400 text-sm">{stat.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="payments" className="space-y-4">
          <TabsList className="bg-slate-900 p-1 rounded-xl flex-wrap h-auto gap-1">
            <TabsTrigger value="payments" className="rounded-lg data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              <DollarSign size={16} className="ml-1" />{t("المدفوعات", "Payments")}
            </TabsTrigger>
            <TabsTrigger value="bookings" className="rounded-lg data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              <Plane size={16} className="ml-1" />{t("الحجوزات", "Bookings")}
            </TabsTrigger>
            <TabsTrigger value="drivers" className="rounded-lg data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              <Car size={16} className="ml-1" />{t("السائقين", "Drivers")}
            </TabsTrigger>
            <TabsTrigger value="properties" className="rounded-lg data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              <Building size={16} className="ml-1" />{t("العقارات", "Properties")}
            </TabsTrigger>
            <TabsTrigger value="vehicles" className="rounded-lg data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              <Car size={16} className="ml-1" />{t("السيارات", "Vehicles")}
            </TabsTrigger>
            <TabsTrigger value="providers" className="rounded-lg data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              <Briefcase size={16} className="ml-1" />{t("مقدمو الخدمات", "Providers")}
            </TabsTrigger>
            <TabsTrigger value="attractions" className="rounded-lg data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              <Sparkles size={16} className="ml-1" />{t("الأماكن", "Tourism")}
            </TabsTrigger>
            <TabsTrigger value="guide" className="rounded-lg data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              <Briefcase size={16} className="ml-1" />{t("الدليل", "Guide")}
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              <Users size={16} className="ml-1" />{t("المستخدمين", "Users")}
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              <Shield size={16} className="ml-1" />{t("الإعدادات", "Settings")}
            </TabsTrigger>
          </TabsList>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <div className="glass-card rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-700 flex justify-between items-center">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <DollarSign className="text-yellow-500" size={20} />
                  {t("سجل المدفوعات", "Payment History")}
                </h3>
                <Badge className="bg-green-500/20 text-green-400">
                  {t("الإيرادات:", "Revenue:")} ${dbSummary?.payments?.revenue || 0}
                </Badge>
              </div>
              <ScrollArea className="h-96">
                <div className="divide-y divide-slate-800">
                  {payments.length === 0 ? (
                    <div className="p-8 text-center text-slate-400">{t("لا توجد مدفوعات", "No payments")}</div>
                  ) : (
                    payments.map((payment) => (
                      <div key={payment.session_id} className="p-4 flex items-center justify-between hover:bg-slate-800/50">
                        <div className="flex-1">
                          <p className="font-medium text-white">{payment.email}</p>
                          <p className="text-sm text-slate-400">{payment.package_id}</p>
                          <p className="text-xs text-slate-500">{new Date(payment.created_at).toLocaleString()}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-green-400">${payment.amount}</span>
                          <Badge className={payment.payment_status === "paid" ? "status-approved" : "status-pending"}>
                            {payment.payment_status}
                          </Badge>
                          {payment.refunded && (
                            <Badge className="bg-red-500/20 text-red-400">{t("مسترد", "Refunded")}</Badge>
                          )}
                          {payment.payment_status === "paid" && !payment.refunded && (
                            <Button
                              size="sm"
                              onClick={() => processRefund(payment.session_id)}
                              disabled={refundingId === payment.session_id}
                              className="bg-red-500/20 text-red-400 hover:bg-red-500/30"
                            >
                              <RotateCcw size={14} className="ml-1" />
                              {refundingId === payment.session_id ? "..." : t("استرداد", "Refund")}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <div className="glass-card rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-700">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <Plane className="text-blue-400" size={20} />
                  {t("حجوزات الرحلات", "Ride Bookings")}
                </h3>
              </div>
              <ScrollArea className="h-96">
                <div className="divide-y divide-slate-800">
                  {bookings.length === 0 ? (
                    <div className="p-8 text-center text-slate-400">{t("لا توجد حجوزات", "No bookings")}</div>
                  ) : (
                    bookings.map((booking) => (
                      <div key={booking.booking_id} className="p-4 hover:bg-slate-800/50">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-medium text-white">{booking.user_name}</p>
                            <p className="text-sm text-slate-400">{booking.user_phone}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-green-400">${booking.price}</span>
                            <Badge className={
                              booking.status === "completed" ? "status-approved" :
                              booking.status === "confirmed" ? "bg-blue-500/20 text-blue-400" :
                              booking.status === "cancelled" ? "status-rejected" : "status-pending"
                            }>
                              {booking.status}
                            </Badge>
                          </div>
                        </div>
                        <div className="bg-slate-800 rounded-lg p-3 mb-3 text-sm">
                          <div className="flex items-center gap-2 text-slate-300">
                            <MapPin size={14} className="text-blue-400" />
                            <span>{booking.pickup_location} → {booking.dropoff_address}</span>
                          </div>
                          <div className="flex items-center gap-2 text-slate-400 mt-1">
                            <Calendar size={14} />
                            <span>{booking.pickup_datetime}</span>
                          </div>
                        </div>
                        {booking.status === "pending" && (
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => updateBookingStatus(booking.booking_id, "confirmed")} className="bg-green-500/20 text-green-400">
                              <Check size={14} className="ml-1" />{t("تأكيد", "Confirm")}
                            </Button>
                            <Button size="sm" onClick={() => updateBookingStatus(booking.booking_id, "cancelled")} className="bg-red-500/20 text-red-400">
                              <X size={14} className="ml-1" />{t("إلغاء", "Cancel")}
                            </Button>
                          </div>
                        )}
                        {booking.status === "confirmed" && (
                          <Button size="sm" onClick={() => updateBookingStatus(booking.booking_id, "completed")} className="bg-blue-500/20 text-blue-400">
                            <Check size={14} className="ml-1" />{t("اكتمل", "Complete")}
                          </Button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          </TabsContent>

          {/* Driver Applications Tab */}
          <TabsContent value="drivers">
            <div className="glass-card rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-700">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <Car className="text-green-400" size={20} />
                  {t("طلبات السائقين", "Driver Applications")}
                </h3>
              </div>
              <ScrollArea className="h-96">
                <div className="divide-y divide-slate-800">
                  {driverApps.length === 0 ? (
                    <div className="p-8 text-center text-slate-400">{t("لا توجد طلبات", "No applications")}</div>
                  ) : (
                    driverApps.map((app) => (
                      <div key={app.application_id} className="p-4 hover:bg-slate-800/50">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-medium text-white">{app.full_name}</p>
                            <p className="text-sm text-slate-400">{app.email}</p>
                            <p className="text-sm text-slate-500">{app.phone}</p>
                          </div>
                          <Badge className={
                            app.status === "approved" ? "status-approved" :
                            app.status === "rejected" ? "status-rejected" : "status-pending"
                          }>
                            {app.status}
                          </Badge>
                        </div>
                        <div className="bg-slate-800 rounded-lg p-3 mb-3 text-sm grid grid-cols-2 gap-2">
                          <div><span className="text-slate-500">{t("المدينة:", "City:")}</span> <span className="text-slate-300">{app.city}, {app.state}</span></div>
                          <div><span className="text-slate-500">{t("الرخصة:", "License:")}</span> <span className="text-slate-300">{app.driver_license_number}</span></div>
                          <div><span className="text-slate-500">{t("السيارة:", "Vehicle:")}</span> <span className="text-slate-300">{app.vehicle_make} {app.vehicle_model} {app.vehicle_year}</span></div>
                          <div><span className="text-slate-500">{t("الخبرة:", "Experience:")}</span> <span className="text-slate-300">{app.years_of_experience} {t("سنة", "years")}</span></div>
                        </div>
                        {app.status === "pending" && (
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => reviewApplication(app.application_id, "approve")} className="bg-green-500 hover:bg-green-600 text-white">
                              <Check size={14} className="ml-1" />{t("قبول", "Approve")}
                            </Button>
                            <Button size="sm" onClick={() => reviewApplication(app.application_id, "reject")} className="bg-red-500 hover:bg-red-600 text-white">
                              <X size={14} className="ml-1" />{t("رفض", "Reject")}
                            </Button>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          </TabsContent>

          {/* Properties Tab */}
          <TabsContent value="properties">
            <div className="glass-card rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-700">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <Building className="text-purple-400" size={20} />
                  {t("إعلانات السكن", "Property Listings")}
                </h3>
              </div>
              <ScrollArea className="h-96">
                <div className="divide-y divide-slate-800">
                  {properties.length === 0 ? (
                    <div className="p-8 text-center text-slate-400">{t("لا توجد عقارات", "No properties")}</div>
                  ) : (
                    properties.map((prop) => (
                      <div key={prop.property_id} className="p-4 hover:bg-slate-800/50">
                        <div className="flex items-start justify-between mb-2 gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-white truncate">{prop.title}</p>
                            <p className="text-sm text-slate-400">{prop.city}, {prop.state} {prop.zip_code} · {prop.property_type}</p>
                            <p className="text-sm text-slate-500">{t("المالك:", "Owner:")} {prop.owner_name} · {prop.phone}</p>
                          </div>
                          <div className="text-left flex-shrink-0">
                            <p className="font-bold text-yellow-400">${prop.price}</p>
                            <Badge className={prop.status === "approved" ? "status-approved" : prop.status === "rejected" ? "status-rejected" : "status-pending"}>
                              {prop.status}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          {prop.status !== "approved" && (
                            <Button size="sm" onClick={() => updatePropertyStatus(prop.property_id, "approved")} className="bg-green-500 hover:bg-green-600 text-white" data-testid={`approve-prop-${prop.property_id}`}>
                              <Check size={14} className="ml-1" />{t("قبول ونشر", "Approve")}
                            </Button>
                          )}
                          {prop.status !== "rejected" && (
                            <Button size="sm" onClick={() => updatePropertyStatus(prop.property_id, "rejected")} className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30">
                              <X size={14} className="ml-1" />{t("رفض", "Reject")}
                            </Button>
                          )}
                          <Button size="sm" onClick={() => deleteProperty(prop.property_id)} className="bg-red-500/20 text-red-400 hover:bg-red-500/30">
                            <X size={14} className="ml-1" />{t("حذف", "Delete")}
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          </TabsContent>

          {/* Vehicles Tab */}
          <TabsContent value="vehicles">
            <div className="glass-card rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-700">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <Car className="text-blue-400" size={20} />
                  {t("إعلانات السيارات", "Vehicle Listings")}
                </h3>
              </div>
              <ScrollArea className="h-96">
                <div className="divide-y divide-slate-800">
                  {vehicles.length === 0 ? (
                    <div className="p-8 text-center text-slate-400">{t("لا توجد سيارات", "No vehicles")}</div>
                  ) : (
                    vehicles.map((v) => (
                      <div key={v.vehicle_id} className="p-4 hover:bg-slate-800/50">
                        <div className="flex items-start justify-between mb-2 gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-white truncate">{v.brand} {v.model} {v.year}</p>
                            <p className="text-sm text-slate-400">{v.city}, {v.state} · {v.listing_type === "rent" ? t("للإيجار", "Rent") : t("للبيع", "Sale")}</p>
                            <p className="text-sm text-slate-500">{t("المالك:", "Owner:")} {v.owner_name} · {v.phone}</p>
                          </div>
                          <div className="text-left flex-shrink-0">
                            <p className="font-bold text-yellow-400">${v.price}</p>
                            <Badge className={v.status === "approved" ? "status-approved" : v.status === "rejected" ? "status-rejected" : "status-pending"}>
                              {v.status}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          {v.status !== "approved" && (
                            <Button size="sm" onClick={() => updateVehicleStatus(v.vehicle_id, "approved")} className="bg-green-500 hover:bg-green-600 text-white" data-testid={`approve-veh-${v.vehicle_id}`}>
                              <Check size={14} className="ml-1" />{t("قبول ونشر", "Approve")}
                            </Button>
                          )}
                          {v.status !== "rejected" && (
                            <Button size="sm" onClick={() => updateVehicleStatus(v.vehicle_id, "rejected")} className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30">
                              <X size={14} className="ml-1" />{t("رفض", "Reject")}
                            </Button>
                          )}
                          <Button size="sm" onClick={() => deleteVehicle(v.vehicle_id)} className="bg-red-500/20 text-red-400 hover:bg-red-500/30">
                            <X size={14} className="ml-1" />{t("حذف", "Delete")}
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="attractions">
            <AdminAttractions />
          </TabsContent>

          {/* Guide Knowledge Base Tab */}
          <TabsContent value="guide">
            <AdminGuide />
          </TabsContent>

          {/* Service Providers Tab */}
          <TabsContent value="providers">
            <div className="glass-card rounded-xl overflow-hidden" data-testid="admin-providers-tab">
              <div className="p-4 border-b border-slate-700">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <Briefcase className="text-yellow-400" size={20} />
                  {t("مقدمو الخدمات", "Service Providers")}
                  <Badge className="bg-slate-800 text-slate-300 mr-2">{providers.length}</Badge>
                </h3>
              </div>
              <ScrollArea className="h-[28rem]">
                <div className="divide-y divide-slate-800">
                  {providers.length === 0 ? (
                    <div className="p-8 text-center text-slate-400">{t("لا يوجد مقدمو خدمات", "No providers")}</div>
                  ) : providers.map((p) => (
                    <div key={p.provider_id} className="p-4 hover:bg-slate-800/50" data-testid={`adm-prov-${p.provider_id}`}>
                      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-white truncate">{p.full_name}</p>
                            {p.verified && <Verified className="text-blue-400" size={14} />}
                            {p.featured && <Sparkles className="text-yellow-400" size={14} />}
                            {p.top_rated && <Award className="text-yellow-400" size={14} />}
                          </div>
                          <p className="text-sm text-slate-400 truncate">{p.business_name || "—"} · {p.profession}</p>
                          <p className="text-xs text-slate-500">{p.city}, {p.state} · {p.zip_code} · {p.phone}</p>
                          {p.rating !== null && (
                            <p className="text-xs text-yellow-400 mt-1">★ {p.rating} ({p.review_count} {t("تقييم", "reviews")})</p>
                          )}
                        </div>
                        <Badge className={p.status === "approved" ? "status-approved" : p.status === "rejected" ? "status-rejected" : "status-pending"}>
                          {p.status}
                        </Badge>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {p.status !== "approved" && (
                          <Button size="sm" onClick={() => updateProviderStatus(p.provider_id, "approved")} className="bg-green-500 hover:bg-green-600 text-white" data-testid={`approve-prov-${p.provider_id}`}>
                            <Check size={14} className="ml-1" />{t("قبول", "Approve")}
                          </Button>
                        )}
                        {p.status !== "rejected" && (
                          <Button size="sm" onClick={() => updateProviderStatus(p.provider_id, "rejected")} className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30" data-testid={`reject-prov-${p.provider_id}`}>
                            <X size={14} className="ml-1" />{t("رفض", "Reject")}
                          </Button>
                        )}
                        <Button size="sm" onClick={() => toggleProviderFlag(p.provider_id, "verified", !p.verified)} className="bg-blue-500/20 text-blue-300 hover:bg-blue-500/30">
                          <Verified size={14} className="ml-1" />{p.verified ? t("إلغاء التوثيق", "Unverify") : t("توثيق", "Verify")}
                        </Button>
                        <Button size="sm" onClick={() => toggleProviderFlag(p.provider_id, "featured", !p.featured)} className="bg-purple-500/20 text-purple-300 hover:bg-purple-500/30">
                          <Sparkles size={14} className="ml-1" />{p.featured ? t("إلغاء التميز", "Unfeature") : t("تمييز", "Feature")}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </TabsContent>

          <TabsContent value="users">
            <div className="glass-card rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-700 flex justify-between items-center">
                <h3 className="font-bold text-white">{t("المستخدمين", "Users")}</h3>
                <div className="relative w-64">
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t("بحث...", "Search...")}
                    className="h-10 bg-slate-800 border-slate-700 text-white pr-10"
                  />
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                </div>
              </div>
              <ScrollArea className="h-96">
                <div className="divide-y divide-slate-800">
                  {users.filter(u => 
                    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
                  ).map((user) => (
                    <div key={user.user_id} className="p-4 flex items-center justify-between hover:bg-slate-800/50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center text-black font-bold">
                          {user.name?.charAt(0) || "U"}
                        </div>
                        <div>
                          <p className="font-medium text-white">{user.name}</p>
                          <p className="text-sm text-slate-400">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {user.subscription_status === "active" && (
                          <Badge className="bg-green-500/20 text-green-400">{t("مشترك", "Subscribed")}</Badge>
                        )}
                        <Badge className={
                          user.role === "admin" ? "bg-red-500/20 text-red-400" :
                          user.role === "driver" ? "bg-green-500/20 text-green-400" :
                          user.role === "property_owner" ? "bg-purple-500/20 text-purple-400" :
                          "bg-slate-500/20 text-slate-400"
                        }>
                          {user.role}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="glass-card rounded-xl p-6">
              <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                <Shield className="text-yellow-500" />
                {t("إضافة مدير جديد", "Add New Admin")}
              </h3>
              <div className="flex gap-3 mb-6">
                <Input
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  placeholder={t("البريد الإلكتروني للمستخدم", "User email")}
                  className="flex-1 h-12 bg-slate-900 border-slate-700 text-white"
                />
                <Button onClick={addAdmin} className="btn-primary">
                  {t("إضافة كمدير", "Add as Admin")}
                </Button>
              </div>

              <div className="border-t border-slate-700 pt-6">
                <h4 className="font-bold text-white mb-3">{t("المديرين الحاليين", "Current Admins")}</h4>
                <div className="space-y-2">
                  {users.filter(u => u.role === "admin").map(admin => (
                    <div key={admin.user_id} className="bg-slate-800 rounded-lg p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Shield className="text-red-400" size={20} />
                        <div>
                          <p className="text-white">{admin.name}</p>
                          <p className="text-sm text-slate-400">{admin.email}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
